import {
  OperationType,
  ProjectResourceType,
  SystemVariableType,
  VariableListItemDto,
  VariableType,
} from 'services/api/api-client';
import RefreshIcon from 'assets/icons/refresh.svg?react';
import RefreshWithChangesIcon from 'assets/icons/has-changes-refresh.svg?react';
import Dots from 'assets/icons/dots.svg?react';

import styles from './VariableCard.module.scss';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  TextButton,
  TextButtonColor,
} from 'components/uikit/buttons/TextButton';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import { RefreshVariableModal } from '../refreshVariableModal/RefreshVariableModal';
import { Tooltip } from '@mui/material';
import { withStyles } from '@mui/styles';
import { format } from 'date-fns';
import { PreviewVariableChangesModal } from '../previewVariableChanges/PreviewVariableChangesModal.tsx';
import { AppMenu } from 'components/uikit/menu/AppMenu.tsx';
import { DeleteVariableModal } from '../deleteVariableModal/DeleteVariableModal.tsx';
import {
  getVariableLockInfo,
  useVariableCardAppMenuItems,
} from './useVariableCardAppMenuItems.tsx';
import { MergeVariableModal } from '../mergeVariableModal/MergeVariableModal.tsx';
import { useDocumentContext } from 'pages/authorized/documents/documentContext/DocumentContext.tsx';
import { PermissionGate } from 'helpers/projectPermissions/PermissionGate.tsx';
import { permission } from 'helpers/projectPermissions/types/projectPermission.ts';
import { useHasProjectPermission } from 'helpers/projectPermissions/useHasProjectPermission.ts';
import EditIcon from 'assets/icons/edit.svg?react';
import Eye from 'assets/icons/read-only-eye.svg?react';
import { clsx } from 'clsx';
import { BrightTooltip } from 'components/tooltips/BrightTooltip.tsx';
import useElementTextOverflow, {
  OverflowDirection,
} from 'helpers/useElementTextOverflow.ts';

export type VariableCardProps = {
  variable: VariableListItemDto;
  revisionId: number;
  projectId: number;
  isReadOnly: boolean;
  onEdit: (id: number) => void;
  onView: (id: number) => void;
  onVariableExport: (id: number, revisionId: number) => void;
  onVariableClick: (() => void) | undefined;
  onVariableUpdate: (variableId: number) => Promise<void>;
  documentId?: number;
  variableManageAllowed: boolean;
  className?: string;
};

const editableSystemVariableTypes = [
  SystemVariableType.Date,
  SystemVariableType.Version,
];

export const VariableCard: React.FC<VariableCardProps> = (props) => {
  const [isDocumentSelectorOpen, setIsDocumentSelectorOpen] = useState(false);
  const [previewChangesState, setPreviewChangesState] = useState<{
    isModalOpen: boolean;
    postfix?: string;
  }>({ isModalOpen: false });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState<boolean>(false);
  const { setDocumentState } = useDocumentContext();

  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const menuAnchorRef = useRef<HTMLElement>(null);

  const i18nVariables = useScopedTranslation('Page.Variables');
  const i18nModal = useScopedTranslation('Component.UpdateVariableModal');

  const getTooltipDate = (date: Date) => format(date, 'PPPp');

  const lockInfo = getVariableLockInfo(
    props.variable.lockStatuses,
    props.documentId,
  );
  const isLockedByOtherEditor = lockInfo.reason !== null;
  const isSystemVariable = props.variable.systemVariableType !== null;
  const ableToEdit =
    props.variableManageAllowed &&
    !isLockedByOtherEditor &&
    (!isSystemVariable ||
      editableSystemVariableTypes.includes(
        props.variable.systemVariableType as SystemVariableType,
      ));

  const refreshVariable = async () => {
    if (props.documentId)
      setDocumentState((prevState) => ({
        ...prevState,
        needToSave: true,
      }));

    setIsDocumentSelectorOpen(true);
  };

  const onShowContextualMenu = useCallback(
    (ev: React.MouseEvent<HTMLElement>) => {
      ev.stopPropagation();
      ev.preventDefault();
      setShowContextualMenu(true);
    },
    [],
  );

  const menuItems = useVariableCardAppMenuItems({
    ...props,
    isLockedByOtherEditor,
    onPreviewUpdate: (postfix) => {
      setPreviewChangesState({ isModalOpen: true, postfix });
    },
    onDelete: () => {
      setIsDeleteModalOpen(true);
    },
    onMerge: () => {
      if (props.documentId)
        setDocumentState((prevState) => ({
          ...prevState,
          needToSave: true,
        }));

      setShowContextualMenu(false);
      setIsMergeModalOpen(true);
    },
  });
  const emptyMenuItems = menuItems.filter((mi) => !mi.hidden).length === 0;

  const hasPermission = useHasProjectPermission();
  const hasUpdateDocumentPermission = hasPermission([
    permission(ProjectResourceType.Document, OperationType.Update),
  ]);

  const refreshVariableTooltip = useMemo(() => {
    if (lockInfo.reason) return lockInfo.reason;

    if (!props.variable.actualValue.hasUpdatedData) return null;

    return (
      <>
        <span>{i18nVariables.t('update_available_tooltip')}</span>
        <span className={styles.tooltipDate}>
          {i18nVariables.t('update_available_tooltip_date', {
            date: getTooltipDate(
              props.variable.actualValue?.updatedDataDetectedDate!,
            ),
          })}
        </span>
      </>
    );
  }, [
    lockInfo.reason,
    props.variable.actualValue.hasUpdatedData,
    props.variable.actualValue.updatedDataDetectedDate,
  ]);

  const [nameRef, isNameOverflowing] = useElementTextOverflow<HTMLDivElement>(
    OverflowDirection.Horizontal,
  );

  return (
    <div
      className={clsx(
        props.onVariableClick &&
          !props.isReadOnly &&
          hasUpdateDocumentPermission
          ? styles.cardContainer
          : styles.staticCard,
        props.className,
      )}
      onClick={() => {
        if (props.isReadOnly || !hasUpdateDocumentPermission) return;
        props.onVariableClick?.();
      }}
    >
      <div className={styles.card} data-test-id="variable-card">
        <div className={styles.name}>
          <BrightTooltip
            placement="top"
            title={props.variable.name}
            enterDelay={300}
            disableHoverListener={!isNameOverflowing}
          >
            <div
              className={styles.noWrapContainer}
              data-test-id="variable-name"
              ref={nameRef}
            >
              {props.variable.name}
            </div>
          </BrightTooltip>
        </div>
        <div className={styles.id}>{props.variable.id}</div>
        {!emptyMenuItems && (
          <div className={styles.dotMenu}>
            <div
              className={styles.dotMenuImage}
              ref={menuAnchorRef as any}
              onClick={onShowContextualMenu}
              data-test-id="dot-menu"
            >
              <Dots />
            </div>
            <AppMenu
              anchorEl={menuAnchorRef.current}
              menuItems={menuItems}
              open={showContextualMenu}
              onClose={() => setShowContextualMenu(false)}
            />
          </div>
        )}
        <div className={styles.value} data-test-id="variable-value">
          {props.variable.actualValue.variableType === VariableType.Auto
            ? props.variable.actualUrl
            : props.variable.actualValue.value}
        </div>
        <StyledTooltip
          placement="bottom-start"
          PopperProps={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -10],
                },
              },
            ],
          }}
          disableHoverListener={!lockInfo.reason}
          title={<div className={styles.tooltipTitle}>{lockInfo.reason}</div>}
        >
          <div
            className={styles.editButtonCell}
            onClick={(e) => {
              e.stopPropagation();

              if (ableToEdit) props.onEdit(props.variable.id);
              else props.onView(props.variable.id);
            }}
          >
            <TextButton
              icon={ableToEdit ? <EditIcon /> : <Eye />}
              className={styles.editButton}
            />
          </div>
        </StyledTooltip>
        {props.variable.actualValue.variableType !== VariableType.Manual &&
          !props.isReadOnly && (
            <PermissionGate
              permissions={[
                permission(ProjectResourceType.Variable, OperationType.Update),
              ]}
            >
              <StyledTooltip
                placement="bottom-end"
                PopperProps={{
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -10],
                      },
                    },
                  ],
                }}
                disableHoverListener={!refreshVariableTooltip}
                title={
                  <div className={styles.tooltipTitle}>
                    {refreshVariableTooltip}
                  </div>
                }
              >
                <div
                  className={styles.refreshButtonCell}
                  onClick={(e) => e.stopPropagation()}
                >
                  <TextButton
                    onClick={async (e) => {
                      e.stopPropagation();
                      await refreshVariable();
                    }}
                    testId="refresh-variable"
                    color={TextButtonColor.Primary}
                    className={styles.refreshIcon}
                    disabled={isLockedByOtherEditor}
                    icon={
                      props.variable.actualValue.hasUpdatedData ? (
                        <RefreshWithChangesIcon />
                      ) : (
                        <RefreshIcon />
                      )
                    }
                  />
                </div>
              </StyledTooltip>
            </PermissionGate>
          )}
      </div>
      <CustomModal
        isOpen={isDocumentSelectorOpen}
        title={i18nModal.t('title')}
        onClose={() => setIsDocumentSelectorOpen(false)}
        isBlocking={true}
      >
        <RefreshVariableModal
          variableId={props.variable.id}
          variableName={props.variable.name}
          projectId={props.projectId}
          revisionId={props.revisionId}
          onVariableUpdate={props.onVariableUpdate}
          onClose={() => setIsDocumentSelectorOpen(false)}
        />
      </CustomModal>
      {props.variable.isTable && previewChangesState.isModalOpen && (
        <PreviewVariableChangesModal
          isOpen={previewChangesState.isModalOpen}
          variableId={props.variable.id}
          revisionId={props.revisionId}
          onClose={() => setPreviewChangesState({ isModalOpen: false })}
          lockStatuses={lockInfo.lockStatuses}
          postfix={previewChangesState.postfix}
          onUpdate={refreshVariable}
        />
      )}
      {isMergeModalOpen && (
        <MergeVariableModal
          isOpen={isMergeModalOpen}
          projectId={props.projectId}
          targetVariableId={props.variable.id}
          revisionId={props.revisionId}
          onClose={() => setIsMergeModalOpen(false)}
        />
      )}
      <DeleteVariableModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
        }}
        id={props.variable.id}
        revisionId={props.revisionId}
      />
    </div>
  );
};

const StyledTooltip = withStyles({
  tooltip: {
    backgroundColor: '#ffffff !important',
    color: '#000000 !important',
    boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.12)',
    borderRadius: '2px',
  },
})(Tooltip);
